import React, { useRef } from 'react';
import { useIsKeyboardFocused, focusOutlinesStyles, getRem, typographyGroupLabel } from 'lib/core';
import { useShareForwardedRef } from 'lib/utilities';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { sliderStyles } from '../sliderStyles';

const StyledWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  height: ${getRem('36px')};
  position: relative;
  width: 100%;
`;

const StyledHiddenInput = styled.input`
  cursor: pointer;
  height: ${getRem('36px')};
  opacity: 0;
  width: 100%;

  &::-ms-tooltip {
    display: none;
  }
`;

const StyledBoundaryValues = styled.span`
  ${({ $isMinValue, theme }) => css`
    ${typographyGroupLabel(theme)};
    margin: ${theme.size.spacing.medium.value};

    ${$isMinValue
      ? css`
          margin-left: 0;
        `
      : css`
          margin-right: 0;
        `}
  `}
`;

const StyledTrack = styled.div`
  background: ${({ $disabled, theme }) => ($disabled ? theme.color.gray[100].value : theme.color.primary[100].value)};
  height: 2px;
  pointer-events: none;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 100%;
`;

const StyledTrackInner = styled.div`
  ${({ $disabled, $value, theme }) => css`
    width: ${$value}%;
    background: ${$disabled ? theme.color.gray[200].value : theme.color.primary[500].value};
  `}
  height: 100%;
`;

const StyledThumb = styled.div`
  ${({ $disabled, $value, theme }) => css`
    background: ${$disabled ? theme.color.gray[200].value : theme.color.primary[500].value};
    height: ${theme.typography.subtitle1.lineHeight.value};
    left: ${$value}%;
    transform: translate(-${$value}%, -50%);
    width: ${theme.typography.subtitle1.lineHeight.value};
  `}
  border-radius: 50%;
  pointer-events: none;
  position: absolute;
  top: 50%;
  ${sliderStyles};
  ${({ $isFocused }) =>
    $isFocused &&
    css`
      ${focusOutlinesStyles.primary};
    `}
`;

const StyledSliderContainer = styled.label`
  align-items: center;
  display: flex;
  position: relative;
  width: 100%;
`;

const SliderControl = React.forwardRef(
  (
    {
      ariaLabel = 'Slider Control',
      dataTestId = '',
      hideMaxVal = false,
      hideMinVal = false,
      id,
      isDisabled = false,
      max = 100,
      min = 0,
      name,
      onChange,
      step = 1,
      value,
      ...other
    },
    ref
  ) => {
    const labelRef = useRef();
    const hiddenInputRef = useShareForwardedRef(ref);

    const isHiddenInputFocused = useIsKeyboardFocused(hiddenInputRef, labelRef);

    const percentage = min === max ? 0 : Math.floor((100 * (value - min)) / (max - min));
    return (
      <StyledWrapper>
        {!hideMinVal && (
          <StyledBoundaryValues data-testid={dataTestId ? `${dataTestId}-min-val` : undefined} $isMinValue>
            {min}
          </StyledBoundaryValues>
        )}
        <StyledSliderContainer
          data-testid={dataTestId ? `${dataTestId}-range-control-label` : undefined}
          htmlFor={id}
          ref={labelRef}
        >
          <StyledHiddenInput
            aria-label={ariaLabel}
            data-testid={dataTestId ? `${dataTestId}-range-control` : undefined}
            disabled={isDisabled}
            value={value}
            $hideLabel
            {...{ id, max, min, name, onChange, step }}
            {...other}
            ref={hiddenInputRef}
            type="range"
          />
          <StyledTrack data-testid={dataTestId ? `${dataTestId}-track` : undefined} $disabled={isDisabled}>
            <StyledTrackInner
              data-testid={dataTestId ? `${dataTestId}-track-inner` : undefined}
              $disabled={isDisabled}
              $value={percentage}
            />
          </StyledTrack>
          <StyledThumb
            data-testid={dataTestId ? `${dataTestId}-thumb` : undefined}
            $disabled={isDisabled}
            $isFocused={isHiddenInputFocused}
            $value={percentage}
          />
        </StyledSliderContainer>
        {!hideMaxVal && (
          <StyledBoundaryValues data-testid={dataTestId ? `${dataTestId}-max-val` : undefined}>
            {max}
          </StyledBoundaryValues>
        )}
      </StyledWrapper>
    );
  }
);

SliderControl.propTypes = {
  /** Informs screen reader users what actions they should take */
  ariaLabel: PropTypes.string,
  /** Id value used for testing */
  dataTestId: PropTypes.string,
  /** If true, max value is hidden */
  hideMaxVal: PropTypes.bool,
  /** If true, min value is hidden */
  hideMinVal: PropTypes.bool,
  /** Identifier of the input component */
  id: PropTypes.string.isRequired,
  /** If true, input is disabled and value of it cannot be edited */
  isDisabled: PropTypes.bool,
  /** Maximum value in the range of permitted values */
  max: PropTypes.number,
  /** Minimum value in the range of permitted values */
  min: PropTypes.number,
  /** Name of the input */
  name: PropTypes.string.isRequired,
  /** Callback to be called when input's value is being changed by user interaction */
  onChange: PropTypes.func.isRequired,
  /** The granularity with which the slider can step through values */
  step: PropTypes.number,
  /** Current value of the slider */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export { SliderControl };
